import { createUID } from '@pega/cosmos-react-core';
import type {
  CategoriesItemProps,
  CategoryItemProps,
  LifeCycleActionParam,
  StageItemProps,
  StepItemProps,
  TaskItemProps
} from '@pega/cosmos-react-build';

export interface LifeCycleMockState {
  activeElementId: string;
  data: {
    id: string;
    categories: CategoriesItemProps[];
    stages: StageItemProps[];
    alternateStages: StageItemProps[];
  };
}

const getStage = (data: LifeCycleActionParam) => {
  return data.stage?.type === 'alternate' ? 'alternateStages' : 'stages';
};

export const addStepCallback = (data: LifeCycleActionParam, lifecycleData: LifeCycleMockState) => {
  const stepLength = data.task?.steps.length;
  const elementID: string = createUID();
  const stepEmptydata: StepItemProps = {
    id: elementID,
    label: 'Step',
    visual: {
      imgSrc: data.task?.steps[stepLength! - 1].visual.imgSrc || '',
      name: data.task?.steps[stepLength! - 1].visual.name || 'information',
      category: data.task?.steps[stepLength! - 1].visual.category || 'task',
      label: data.task?.steps[stepLength! - 1].label || 'Info',
      inverted: data.task?.steps[stepLength! - 1].visual.inverted
    },
    error: '',
    status: { label: '', type: '' }
  };
  data.value = stepEmptydata.label;
  const stagetype = getStage(data);
  const dataClone = { ...lifecycleData };
  dataClone.activeElementId = elementID;
  dataClone.data[stagetype].forEach((stageItems: StageItemProps) => {
    if (stageItems.id === data.stage?.id) {
      stageItems.categories.forEach(categoryItem => {
        if (categoryItem.categoryId === data.category?.categoryId) {
          categoryItem.tasks.forEach(taskItem => {
            if (Array.isArray(taskItem)) {
              taskItem.forEach(item => {
                if (item.id === data?.task?.id) {
                  item.steps.push(stepEmptydata);
                }
              });
            } else if (taskItem.id === data?.task?.id) {
              taskItem.steps.push(stepEmptydata);
            }
          });
        }
      });
    }
  });

  return { dataClone, newStepData: { ...data, id: stepEmptydata.id, step: stepEmptydata } };
};

export const deleteStepCallback = (
  data: LifeCycleActionParam,
  lifecycleData: LifeCycleMockState
) => {
  const stagetype = getStage(data);
  const dataClone = { ...lifecycleData };
  dataClone.data[stagetype].forEach((stageItems: StageItemProps) => {
    if (stageItems.id === data.stage?.id) {
      stageItems.categories.forEach(categoryItem => {
        if (categoryItem.categoryId === data.category?.categoryId) {
          let stepIndex = 0;
          categoryItem.tasks.forEach(taskItem => {
            if (Array.isArray(taskItem)) {
              taskItem.forEach((item: TaskItemProps) => {
                if (item.id === data.task?.id) {
                  item.steps.forEach((stepItem: StepItemProps, index: number) => {
                    if (stepItem.id === data.step?.id) {
                      stepIndex = index;
                    }
                  });
                  item.steps.splice(stepIndex, 1);
                }
              });
            } else if (taskItem.id === data.task?.id) {
              taskItem.steps.forEach((stepItem: StepItemProps, stepItemIndex: number) => {
                if (stepItem.id === data.step?.id) {
                  stepIndex = stepItemIndex;
                }
              });
              taskItem.steps.splice(stepIndex, 1);
            }
          });
        }
      });
    }
  });
  dataClone.activeElementId = '';
  return dataClone;
};

export const updateStepCallback = (
  data: LifeCycleActionParam,
  lifeCycleData: LifeCycleMockState
) => {
  const stagetype = getStage(data);
  const dataClone = { ...lifeCycleData };
  dataClone.activeElementId = '';
  dataClone.data[stagetype].forEach((stageItems: StageItemProps) => {
    if (stageItems.id === data.stage?.id) {
      stageItems.categories.forEach(categoryItem => {
        if (categoryItem.categoryId === data.category?.categoryId) {
          categoryItem.tasks.forEach(taskItem => {
            if (Array.isArray(taskItem)) {
              taskItem.forEach(item => {
                if (item.id === data?.task?.id) {
                  item.steps.forEach(step => {
                    if (step.id === data.step?.id && data.value !== undefined) {
                      step.label = data.value;
                    }
                  });
                }
              });
            } else if (taskItem.id === data?.task?.id) {
              taskItem.steps.forEach(step => {
                if (step.id === data.step?.id && data.value !== undefined) {
                  step.label = data?.value;
                }
              });
            }
          });
        }
      });
    }
  });
  return dataClone;
};

export const addTaskCallback = (
  data: LifeCycleActionParam,
  lifecycleData: LifeCycleMockState,
  parallel: boolean = false
) => {
  const elementID: string = createUID();
  let imgSrc = '';
  if (data.stage && data.stage.categories.length > 1) {
    imgSrc = 'https://pbs.twimg.com/profile_images/824369446798106628/_biA5GRt_400x400.jpg';
  }
  const taskEmptydata: TaskItemProps = {
    id: elementID,
    label: 'Process',
    visual: {
      imgSrc
    },
    error: '',
    steps: []
  };
  data.value = taskEmptydata.label;

  const stagetype = getStage(data);
  const dataClone = { ...lifecycleData };
  const stageItems = dataClone.data[stagetype];
  dataClone.activeElementId = elementID;
  stageItems.forEach((stageItem: StageItemProps) => {
    const [category] = stageItem.categories;
    if (stageItem.id === data.stage?.id) {
      if (stageItem.categories.length > 1) {
        stageItem.categories.forEach(categoryItem => {
          const tasks = categoryItem.tasks as TaskItemProps[];

          if (categoryItem.categoryId === data.category?.categoryId) {
            data.category = category;
            tasks.push(taskEmptydata);
          }
        });
      } else if (parallel || stageItem.categories[0].tasks.length === 0) {
        data.category = category;
        (stageItem.categories[0].tasks as TaskItemProps[][]).push([taskEmptydata]);
      } else {
        data.category = category;
        (stageItem.categories[0].tasks[0] as TaskItemProps[]).push(taskEmptydata);
      }
    }
  });
  return { dataClone, newTaskData: { ...data, id: taskEmptydata.id, task: taskEmptydata } };
};

export const deleteTaskCallback = (
  data: LifeCycleActionParam,
  lifecycleData: LifeCycleMockState
) => {
  const stagetype = getStage(data);
  const dataClone = { ...lifecycleData };
  dataClone.data[stagetype].forEach((stageItems: StageItemProps) => {
    if (stageItems.id === data.stage?.id) {
      stageItems.categories.forEach(categoryItem => {
        if (categoryItem.categoryId === data.category?.categoryId) {
          let taskIndex: number;
          categoryItem.tasks.forEach((taskItem, taskItemIndex: number) => {
            if (Array.isArray(taskItem)) {
              taskItem.forEach((item, index: number) => {
                if (item.id === data?.task?.id) {
                  taskIndex = index;
                }
              });
              if (
                Array.isArray(categoryItem.tasks[0]) &&
                categoryItem.tasks[0].length > 1 &&
                taskIndex
              ) {
                categoryItem.tasks[0].splice(taskIndex, 1);
              } else if (taskItem[0].id === data?.task?.id) {
                categoryItem.tasks.splice(taskItemIndex, 1);
              }
            } else if (taskItem.id === data?.task?.id) {
              categoryItem.tasks.splice(taskItemIndex, 1);
            }
          });
        }
      });
    }
  });
  dataClone.activeElementId = '';
  return dataClone;
};

export const updateTaskCallback = (
  data: LifeCycleActionParam,
  lifeCycleData: LifeCycleMockState
) => {
  const stagetype = getStage(data);
  const dataClone = { ...lifeCycleData };
  dataClone.activeElementId = '';
  dataClone.data[stagetype].forEach((stageItems: StageItemProps) => {
    if (stageItems.id === data.stage?.id) {
      stageItems.categories.forEach(categoryItem => {
        if (categoryItem.categoryId === data.category?.categoryId) {
          categoryItem.tasks.forEach(taskItem => {
            if (Array.isArray(taskItem)) {
              taskItem.forEach(item => {
                if (item.id === data?.task?.id && data.value) {
                  item.label = data.value;
                }
              });
            } else if (taskItem.id === data?.task?.id && data.value) {
              taskItem.label = data.value;
            }
          });
        }
      });
    }
  });
  return dataClone;
};

export const addStageCallback = (
  lifecycleData: LifeCycleMockState,
  stageType: 'stages' | 'alternateStages'
) => {
  let type = '';
  if (stageType === 'stages') {
    type = 'default';
  } else {
    type = 'alternate';
  }
  let categoryArray: CategoryItemProps[] = [];
  const elementID = createUID();
  const personaCategoryID = createUID();
  if (lifecycleData.data.categories.length === 0) {
    categoryArray = [
      {
        id: personaCategoryID,
        categoryId: 'Category1',
        tasks: []
      }
    ];
  } else {
    const dataCategoryID = createUID();
    categoryArray = [
      {
        id: personaCategoryID,
        categoryId: 'Category1',
        tasks: []
      },
      {
        id: dataCategoryID,
        categoryId: 'Category2',
        tasks: []
      }
    ];
  }
  const stagedata = {
    id: elementID,
    label: type === 'default' ? 'Stage' : 'Alternate Stage',
    type,
    error: '',
    categories: categoryArray
  } as StageItemProps;
  const dataClone = { ...lifecycleData };
  dataClone.activeElementId = elementID;
  dataClone.data[stageType].push(stagedata);

  return {
    dataClone,
    newStageData: {
      value: stagedata.label,
      stage: stagedata,
      id: stagedata.id
    }
  };
};

export const deleteStageCallback = (
  data: LifeCycleActionParam,
  lifecycleData: LifeCycleMockState
) => {
  const stagetype = getStage(data);
  const dataClone = { ...lifecycleData };
  let stageIndex = 0;
  dataClone.data[stagetype].forEach((stageItem: StageItemProps, index: number) => {
    if (stageItem.id === data?.stage?.id) {
      stageIndex = index;
    }
  });
  if (stageIndex === 0 && stagetype !== 'alternateStages') {
    return 'firstStage';
  }
  dataClone.activeElementId = '';
  dataClone.data[stagetype].splice(stageIndex, 1);

  return dataClone;
};

export const updateStageCallback = (
  data: LifeCycleActionParam,
  lifeCycleData: LifeCycleMockState
) => {
  const stagetype = getStage(data);
  const dataClone = { ...lifeCycleData };
  dataClone.activeElementId = '';
  dataClone.data[stagetype].forEach((stageItem: StageItemProps) => {
    if (stageItem.id === data.stage?.id && data.value) {
      stageItem.label = data.value;
    }
  });
  return dataClone;
};
